import { useEffect, useRef, useState } from 'react';

interface UseIntersectionObserverOptions {
  /**
   * The root margin for the intersection observer
   * @default '100px'
   */
  rootMargin?: string;
  /**
   * The threshold for the intersection observer
   * @default 0.1
   */
  threshold?: number;
  /**
   * Whether the observer is enabled
   * @default true
   */
  enabled?: boolean;
}

interface UseIntersectionObserverReturn {
  /**
   * Ref to attach to the target element
   */
  ref: (node: HTMLElement | null) => void;
  /**
   * Whether the target element is intersecting
   */
  isIntersecting: boolean;
  /**
   * The intersection observer entry
   */
  entry: IntersectionObserverEntry | null;
}

/**
 * Hook for detecting when an element enters the viewport
 * Useful for implementing infinite scroll and lazy loading
 */
export function useIntersectionObserver({
  rootMargin = '100px',
  threshold = 0.1,
  enabled = true
}: UseIntersectionObserverOptions = {}): UseIntersectionObserverReturn {
  const [entry, setEntry] = useState<IntersectionObserverEntry | null>(null);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [node, setNode] = useState<HTMLElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Callback ref to set the target node
  const ref = (node: HTMLElement | null) => {
    setNode(node);
  };

  useEffect(() => {
    // Clean up previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    // Don't create observer if disabled or no node
    if (!enabled || !node) {
      setEntry(null);
      setIsIntersecting(false);
      return;
    }

    // Create new observer
    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        setEntry(entry);
        setIsIntersecting(entry.isIntersecting);
      },
      {
        rootMargin,
        threshold
      }
    );

    // Start observing
    observerRef.current.observe(node);

    // Cleanup function
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      setEntry(null);
      setIsIntersecting(false);
    };
  }, [node, rootMargin, threshold, enabled]);

  return {
    ref,
    isIntersecting,
    entry
  };
}
